import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FORM_CONTROL_FOCUS_CLASS,
  FORM_FILTER_HOVER_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Permit } from '@/components/guards/Permit'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DateRangeFilter } from '@/components/clients/tabs/DateRangeFilter'
import { TopbarSearchIcon } from '@/components/icons/topbar'
import { cn } from '@/lib/utils'
import type { ClientListQuery } from '@/types'

const ALL = '__all__'

// Client source filter options (aligned with create/edit).
const SOURCE_FILTERS = [
  'direct_call',
  'website',
  'recommendation',
  'google_reviews',
  'on_site_visit',
  'partner',
  'other',
] as const

// Figma 2881:33979 — toolbar control chrome (search / export / selects).
const controlBorder = 'border border-[#e7e9ef] bg-white'
const filterTriggerClass = cn(
  'h-10 w-auto shrink-0 justify-between gap-2 rounded-xl py-3 pl-4 pr-3',
  'text-sm font-normal leading-[1.4] text-[#171a20]',
  controlBorder,
  'focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0',
  'data-[state=open]:ring-0 [&_svg]:size-4 [&_svg]:opacity-100 [&_svg]:text-[#272c36]',
  FORM_FILTER_HOVER_CLASS
)

export interface ClientListFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  filters: Partial<
    Pick<
      ClientListQuery,
      'type' | 'status' | 'source' | 'created_from' | 'created_to'
    >
  >
  setFilters: (
    next: Partial<Omit<ClientListQuery, 'page' | 'limit' | 'search'>>
  ) => void
  onExport: () => void
  className?: string
}

// Clients list toolbar (Figma 2881:33979): Search → Export → Created Date →
// Type → Status → Source.
export function ClientListFilters({
  search,
  onSearchChange,
  filters,
  setFilters,
  onExport,
  className,
}: ClientListFiltersProps) {
  const { t } = useTranslation('clients')
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false)

  const closeDateFilterWhenOpen = (open: boolean) => {
    if (open) setIsDateFilterOpen(false)
  }

  return (
    <div
      className={cn('flex shrink-0 flex-wrap items-center gap-3', className)}
    >
      <div className='relative w-full min-w-50 max-w-full sm:w-[320px]'>
        <TopbarSearchIcon className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#272c36]' />
        <Input
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder={t('list.searchPlaceholder')}
          maxLength={100}
          className={cn(
            'h-10 w-full rounded-xl py-3 pl-10 pr-4 text-sm font-normal leading-[1.4] text-[#171a20]',
            'placeholder:text-[#3d434e]',
            controlBorder,
            FORM_CONTROL_FOCUS_CLASS,
            FORM_FILTER_HOVER_CLASS
          )}
        />
      </div>

      <Permit module='clients' action='view'>
        <Button
          type='button'
          variant='outline'
          className={cn(
            'h-10 shrink-0 gap-2 rounded-xl px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20]',
            controlBorder,
            'hover:bg-accent'
          )}
          onClick={onExport}
        >
          <Download className='size-4 text-[#272c36]' strokeWidth={1.6} />
          {t('list.export')}
        </Button>
      </Permit>

      <DateRangeFilter
        appearance='field'
        labelKey='list.filters.createdDate'
        from={filters.created_from}
        to={filters.created_to}
        open={isDateFilterOpen}
        onOpenChange={setIsDateFilterOpen}
        triggerClassName={cn(
          'h-10 gap-2 rounded-xl border-[#e7e9ef] py-3 pl-4 pr-3 text-sm font-normal leading-[1.4] text-[#171a20]',
          '[&_svg]:text-[#272c36]',
          FORM_CONTROL_FOCUS_CLASS
        )}
        onChange={next =>
          setFilters({
            created_from: next.date_from,
            created_to: next.date_to,
          })
        }
      />

      <Select
        value={filters.type ?? ALL}
        onOpenChange={closeDateFilterWhenOpen}
        onValueChange={v =>
          setFilters({
            type: v === ALL ? undefined : (v as ClientListQuery['type']),
          })
        }
      >
        <SelectTrigger className={filterTriggerClass}>
          <SelectValue placeholder={t('list.filters.allTypes')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('list.filters.allTypes')}</SelectItem>
          <SelectItem value='individual'>
            {t('list.filters.individual')}
          </SelectItem>
          <SelectItem value='company'>{t('list.filters.business')}</SelectItem>
          <SelectItem value='property_management'>
            {t('list.filters.propertyManagement')}
          </SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.status ?? ALL}
        onOpenChange={closeDateFilterWhenOpen}
        onValueChange={v => setFilters({ status: v === ALL ? undefined : v })}
      >
        <SelectTrigger className={filterTriggerClass}>
          <SelectValue placeholder={t('list.filters.allStatuses')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('list.filters.allStatuses')}</SelectItem>
          <SelectItem value='active'>{t('list.filters.active')}</SelectItem>
          <SelectItem value='inactive'>{t('list.filters.inactive')}</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.source ?? ALL}
        onOpenChange={closeDateFilterWhenOpen}
        onValueChange={v => setFilters({ source: v === ALL ? undefined : v })}
      >
        <SelectTrigger className={filterTriggerClass}>
          <SelectValue placeholder={t('list.filters.allSources')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('list.filters.allSources')}</SelectItem>
          {SOURCE_FILTERS.map(s => (
            <SelectItem key={s} value={s}>
              {t(`sourceLabels.${s}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export default ClientListFilters
